import { apiFetch, apiUploadFormData } from "@/auth/api";
import type {
  AnexoAtendimento,
  Aso,
  AsoDoAtendimento,
  AtendimentoCriado,
  AtendimentoDetalhe,
  AtendimentoResumo,
  Colaborador,
  Empresa,
  Exame,
  ExameRealizado,
  FichaClinica,
  Funcao,
  Medico,
  Parecer,
  Pendencia,
  ResultadoParecer,
  Setor,
  SituacaoExame,
  StatusAtendimento,
  TipoAnexo,
  TipoExameOcupacional,
  Unidade,
} from "@/types/api";
import type {
  DocumentoDetalhe,
  DocumentoPrograma,
  ExtracaoPayload,
  TipoDocumento,
} from "@/types/extracao";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ---------- Empresas ----------
export function useEmpresas(filtros: { q?: string; incluir_inativas?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filtros.q) params.set("q", filtros.q);
  if (filtros.incluir_inativas) params.set("incluir_inativas", "true");
  return useQuery<Empresa[]>({
    queryKey: ["empresas", filtros],
    queryFn: () => apiFetch<Empresa[]>(`/empresas?${params.toString()}`),
  });
}

export function useEmpresa(id: number | null) {
  return useQuery<Empresa>({
    queryKey: ["empresas", id],
    queryFn: () => apiFetch<Empresa>(`/empresas/${id}`),
    enabled: id !== null,
  });
}

export interface EmpresaCreatePayload {
  razao_social: string;
  cnpj: string;
  endereco?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
  cnae?: string | null;
  grau_risco?: number | null;
}

export function useCriarEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmpresaCreatePayload) =>
      apiFetch<Empresa>("/empresas", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["empresas"] }),
  });
}

export function useAtualizarEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Empresa> }) =>
      apiFetch<Empresa>(`/empresas/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["empresas"] }),
  });
}

export function useDesativarEmpresa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<void>(`/empresas/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["empresas"] }),
  });
}

// ---------- Unidades (aninhadas por empresa) ----------
export function useUnidades(empresaId: number | null) {
  return useQuery<Unidade[]>({
    queryKey: ["empresas", empresaId, "unidades"],
    queryFn: () => apiFetch<Unidade[]>(`/empresas/${empresaId}/unidades`),
    enabled: empresaId !== null,
  });
}

export function useCriarUnidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ empresaId, body }: { empresaId: number; body: Partial<Unidade> }) =>
      apiFetch<Unidade>(`/empresas/${empresaId}/unidades`, { method: "POST", json: body }),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["empresas", v.empresaId, "unidades"] }),
  });
}

export function useAtualizarUnidade(empresaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Unidade> }) =>
      apiFetch<Unidade>(`/unidades/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["empresas", empresaId, "unidades"] }),
  });
}

// ---------- Setores (aninhados por unidade) ----------
export function useSetores(unidadeId: number | null) {
  return useQuery<Setor[]>({
    queryKey: ["unidades", unidadeId, "setores"],
    queryFn: () => apiFetch<Setor[]>(`/unidades/${unidadeId}/setores`),
    enabled: unidadeId !== null,
  });
}

export function useCriarSetor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ unidadeId, body }: { unidadeId: number; body: { nome: string } }) =>
      apiFetch<Setor>(`/unidades/${unidadeId}/setores`, { method: "POST", json: body }),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["unidades", v.unidadeId, "setores"] }),
  });
}

// ---------- Funções (aninhadas por setor) ----------
export function useFuncoes(setorId: number | null) {
  return useQuery<Funcao[]>({
    queryKey: ["setores", setorId, "funcoes"],
    queryFn: () => apiFetch<Funcao[]>(`/setores/${setorId}/funcoes`),
    enabled: setorId !== null,
  });
}

export function useCriarFuncao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ setorId, body }: { setorId: number; body: { nome: string; cbo?: string | null } }) =>
      apiFetch<Funcao>(`/setores/${setorId}/funcoes`, { method: "POST", json: body }),
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["setores", v.setorId, "funcoes"] }),
  });
}

// ---------- Documentos (PCMSO/PGR — Importação Inteligente) ----------
/** Lista os documentos da empresa; enquanto houver extração 'processando', faz polling a cada 4s. */
export function useDocumentos(empresaId: number | null) {
  return useQuery<DocumentoPrograma[]>({
    queryKey: ["empresas", empresaId, "documentos"],
    queryFn: () => apiFetch<DocumentoPrograma[]>(`/empresas/${empresaId}/documentos`),
    enabled: empresaId !== null,
    refetchInterval: (query) =>
      query.state.data?.some((d) => d.extracao_status === "processando") ? 4000 : false,
  });
}

export function useDocumento(id: number | null) {
  return useQuery<DocumentoDetalhe>({
    queryKey: ["documentos", id],
    queryFn: () => apiFetch<DocumentoDetalhe>(`/documentos/${id}`),
    enabled: id !== null,
  });
}

export function useUploadDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ empresaId, tipo, escopo, arquivo }: {
      empresaId: number;
      tipo: TipoDocumento;
      escopo?: string;
      arquivo: File;
    }) => {
      const fd = new FormData();
      fd.set("tipo", tipo);
      // escopo vai no MESMO multipart (o backend aceita o campo) — sem PATCH extra
      fd.set("escopo", escopo ?? "");
      fd.set("arquivo", arquivo);
      return apiUploadFormData<DocumentoPrograma>(`/empresas/${empresaId}/documentos`, fd);
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["empresas", v.empresaId, "documentos"] }),
  });
}

/** Escopo (contrato/lotação) do documento — coluna própria, fora do JSON da extração. */
export function useAtualizarDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, escopo }: { id: number; escopo: string }) =>
      apiFetch<DocumentoDetalhe>(`/documentos/${id}`, { method: "PATCH", json: { escopo } }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["documentos", d.id] });
      qc.invalidateQueries({ queryKey: ["empresas", d.empresa_id, "documentos"] });
    },
  });
}

export function useSalvarRevisao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ExtracaoPayload }) =>
      apiFetch<DocumentoDetalhe>(`/documentos/${id}/extracao`, { method: "PATCH", json: payload }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["documentos", v.id] });
      qc.invalidateQueries({ queryKey: ["empresas"] });
    },
  });
}

export function useReprocessarDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<DocumentoPrograma>(`/documentos/${id}/reprocessar`, { method: "POST" }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["documentos", d.id] });
      qc.invalidateQueries({ queryKey: ["empresas", d.empresa_id, "documentos"] });
    },
  });
}

/**
 * Tira o documento aprovado de vigência (para de gerar pendências) sem apagar nada.
 * Necessário quando a empresa unifica/reparte PCMSOs e os escopos não se
 * correspondem — aí a substituição automática não alcança o documento antigo.
 */
export interface MedicoCoordenadorPayload {
  nome: string;
  crm: string;
  uf: string;
  aplicar_em_todos_da_empresa?: boolean;
}

/** Troca o médico responsável pelo PCMSO — vale inclusive em documento
 *  aprovado, porque é esse nome que sai impresso em todo ASO novo.
 *  ASOs já emitidos não mudam: o snapshot é congelado na emissão. */
export function useTrocarMedicoCoordenador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: MedicoCoordenadorPayload & { id: number }) =>
      apiFetch<DocumentoPrograma[]>(`/documentos/${id}/medico-coordenador`, {
        method: "PATCH",
        json: body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documentos"] }),
  });
}

export function useInativarDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<DocumentoPrograma>(`/documentos/${id}/inativar`, { method: "POST" }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["documentos", d.id] });
      qc.invalidateQueries({ queryKey: ["empresas", d.empresa_id, "documentos"] });
    },
  });
}

/** Devolve o documento inativo à vigência — 409 se já houver aprovado no mesmo escopo. */
export function useReativarDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<DocumentoPrograma>(`/documentos/${id}/reativar`, { method: "POST" }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["documentos", d.id] });
      qc.invalidateQueries({ queryKey: ["empresas", d.empresa_id, "documentos"] });
    },
  });
}

/** Exclui documento ainda NÃO aprovado (upload errado, duplicado, erro de extração). */
export function useExcluirDocumento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; empresaId: number }) =>
      apiFetch<void>(`/documentos/${id}`, { method: "DELETE" }),
    onSuccess: (_r, { id, empresaId }) => {
      qc.invalidateQueries({ queryKey: ["documentos", id] });
      qc.invalidateQueries({ queryKey: ["empresas", empresaId, "documentos"] });
    },
  });
}

export function useAprovarDocumento() {
  const qc = useQueryClient();
  return useMutation({
    // escopo vai no corpo: a substituição do documento anterior é por
    // (empresa, tipo, escopo) — contratos irmãos seguem vigentes.
    mutationFn: ({ id, escopo }: { id: number; escopo: string }) =>
      apiFetch<DocumentoPrograma>(`/documentos/${id}/aprovar`, {
        method: "POST",
        json: { escopo },
      }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["documentos", d.id] });
      qc.invalidateQueries({ queryKey: ["empresas", d.empresa_id, "documentos"] });
    },
  });
}

// ---------- Médicos ----------
export function useMedicos(q?: string) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  return useQuery<Medico[]>({
    queryKey: ["medicos", q ?? null],
    queryFn: () => apiFetch<Medico[]>(`/medicos?${params.toString()}`),
  });
}

export interface MedicoCreatePayload {
  nome: string;
  crm: string;
  uf_crm: string;
  rqe?: string | null;
  especialidade?: string | null;
}

export function useCriarMedico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MedicoCreatePayload) =>
      apiFetch<Medico>("/medicos", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medicos"] }),
  });
}

export function useAtualizarMedico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Medico> }) =>
      apiFetch<Medico>(`/medicos/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["medicos"] }),
  });
}

// ---------- Colaboradores ----------
export function useColaboradores(
  filtros: { empresa_id?: number | null; q?: string; incluir_inativos?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (filtros.empresa_id != null) params.set("empresa_id", String(filtros.empresa_id));
  if (filtros.q) params.set("q", filtros.q);
  if (filtros.incluir_inativos) params.set("incluir_inativos", "true");
  return useQuery<Colaborador[]>({
    queryKey: ["colaboradores", filtros],
    queryFn: () => apiFetch<Colaborador[]>(`/colaboradores?${params.toString()}`),
  });
}

export interface ColaboradorCreatePayload {
  empresa_id: number;
  unidade_id: number;
  setor_id: number;
  funcao_id: number;
  nome: string;
  cpf: string;
  rg?: string | null;
  data_nascimento?: string | null;
  sexo?: "M" | "F" | null;
}

export function useCriarColaborador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ColaboradorCreatePayload) =>
      apiFetch<Colaborador>("/colaboradores", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["colaboradores"] }),
  });
}

export function useAtualizarColaborador() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Colaborador> }) =>
      apiFetch<Colaborador>(`/colaboradores/${id}`, { method: "PATCH", json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["colaboradores"] }),
  });
}

// ---------- Pendências (vw_situacao_exames) ----------
export interface PendenciasFiltros {
  empresa_id?: number | null;
  situacao?: SituacaoExame | "";
  q?: string;
  colaborador_id?: number | null;
}

export function usePendencias(filtros: PendenciasFiltros = {}, enabled = true) {
  const params = new URLSearchParams();
  if (filtros.empresa_id != null) params.set("empresa_id", String(filtros.empresa_id));
  if (filtros.situacao) params.set("situacao", filtros.situacao);
  if (filtros.q) params.set("q", filtros.q);
  if (filtros.colaborador_id != null) params.set("colaborador_id", String(filtros.colaborador_id));
  if (filtros.q) params.set("q", filtros.q);
  return useQuery<Pendencia[]>({
    queryKey: ["pendencias", filtros],
    queryFn: () => apiFetch<Pendencia[]>(`/pendencias?${params.toString()}`),
    enabled,
  });
}

// ---------- Atendimentos ----------
export interface AtendimentosFiltros {
  status?: StatusAtendimento | "";
  data?: string;
  colaborador_id?: number | null;
  /** nome ou CPF do colaborador */
  q?: string;
}

export function useAtendimentos(filtros: AtendimentosFiltros = {}) {
  const params = new URLSearchParams();
  if (filtros.status) params.set("status", filtros.status);
  if (filtros.data) params.set("data", filtros.data);
  if (filtros.colaborador_id != null) params.set("colaborador_id", String(filtros.colaborador_id));
  if (filtros.q) params.set("q", filtros.q);
  return useQuery<AtendimentoResumo[]>({
    queryKey: ["atendimentos", filtros],
    queryFn: () => apiFetch<AtendimentoResumo[]>(`/atendimentos?${params.toString()}`),
  });
}

export function useAtendimento(id: number | null) {
  return useQuery<AtendimentoDetalhe>({
    queryKey: ["atendimentos", id],
    queryFn: () => apiFetch<AtendimentoDetalhe>(`/atendimentos/${id}`),
    enabled: id !== null,
  });
}

export interface AtendimentoCreatePayload {
  tipo: TipoExameOcupacional;
  medico_id: number;
}

export function useCriarAtendimento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ colaboradorId, body }: { colaboradorId: number; body: AtendimentoCreatePayload }) =>
      apiFetch<AtendimentoCriado>(`/colaboradores/${colaboradorId}/atendimentos`, {
        method: "POST",
        json: body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["atendimentos"] }),
  });
}

export interface FichaPayload {
  anamnese?: string | null;
  exame_fisico?: string | null;
  antecedentes?: string | null;
}

export function useSalvarFicha(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FichaPayload) =>
      apiFetch<FichaClinica>(`/atendimentos/${atendimentoId}/ficha`, { method: "PUT", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] }),
  });
}

export interface ExameRealizadoPayload {
  exame_id: number;
  data_realizacao: string;
  resultado?: string | null;
  observacao?: string | null;
  solicitado_pela_empresa?: boolean;
}

// ---------- Catálogo de exames ----------
// Existe pros exames que a EMPRESA pede fora da bateria do PCMSO: nem sempre
// estão no catálogo, então a tela busca e, se não achar, cadastra na hora.
export function useExames(q: string) {
  return useQuery<Exame[]>({
    queryKey: ["exames", q],
    queryFn: () => apiFetch<Exame[]>(`/exames?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
  });
}

/** Catálogo inteiro — alimenta o 2º grupo do seletor de exames do atendimento.
 *  Nem tudo que o médico faz está na bateria do GHE: às vezes sai só o exame
 *  clínico, às vezes um exame a mais. A API já aceita qualquer exame; o que
 *  faltava era a tela oferecer. */
/** Corrige o médico escolhido errado na abertura do atendimento.
 *  Vale só enquanto o atendimento está em aberto e ANTES do parecer. */
export function useTrocarMedicoAtendimento(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (medico_id: number) =>
      apiFetch(`/atendimentos/${atendimentoId}/medico`, {
        method: "PATCH",
        json: { medico_id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] }),
  });
}

export function useExamesParaSelecao() {
  return useQuery<Exame[]>({
    queryKey: ["exames", "selecao"],
    queryFn: () => apiFetch<Exame[]>("/exames?limit=200"),
  });
}

export function useCriarExame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nome: string) =>
      apiFetch<Exame>("/exames", { method: "POST", json: { nome } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exames"] }),
  });
}

export function useAdicionarExameRealizado(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExameRealizadoPayload) =>
      apiFetch<ExameRealizado>(`/atendimentos/${atendimentoId}/exames-realizados`, {
        method: "POST",
        json: body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] });
      qc.invalidateQueries({ queryKey: ["pendencias"] });
    },
  });
}

export function useRemoverExameRealizado(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (exameRealizadoId: number) =>
      apiFetch<void>(`/exames-realizados/${exameRealizadoId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] });
      qc.invalidateQueries({ queryKey: ["pendencias"] });
    },
  });
}

export interface ParecerPayload {
  resultado: ResultadoParecer;
  restricoes?: string | null;
  aptidoes: { aptidao_id: number; apto: boolean }[];
}

export function useEmitirParecer(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ParecerPayload) =>
      apiFetch<Parecer>(`/atendimentos/${atendimentoId}/parecer`, { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["atendimentos"] }),
  });
}

export function useCancelarAtendimento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ atendimentoId, motivo }: { atendimentoId: number; motivo: string }) =>
      apiFetch<AtendimentoCriado>(`/atendimentos/${atendimentoId}/cancelar`, {
        method: "POST",
        json: { motivo },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["atendimentos"] }),
  });
}

// ---------- Anexos do atendimento (digitalização do papel assinado) ----------
export function useAnexos(atendimentoId: number | null) {
  return useQuery<AnexoAtendimento[]>({
    queryKey: ["atendimentos", atendimentoId, "anexos"],
    queryFn: () => apiFetch<AnexoAtendimento[]>(`/atendimentos/${atendimentoId}/anexos`),
    enabled: atendimentoId !== null,
  });
}

export interface AnexoUploadPayload {
  tipo: TipoAnexo;
  descricao?: string;
  arquivo: File;
}

export function useEnviarAnexo(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, descricao, arquivo }: AnexoUploadPayload) => {
      const fd = new FormData();
      fd.set("tipo", tipo);
      if (descricao) fd.set("descricao", descricao);
      fd.set("arquivo", arquivo);
      return apiUploadFormData<AnexoAtendimento>(
        `/atendimentos/${atendimentoId}/anexos`,
        fd
      );
    },
    // O detalhe também recarrega: anexar `aso_assinado` habilita "marcar como assinado".
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId, "anexos"] });
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] });
    },
  });
}

export function useRemoverAnexo(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (anexoId: number) =>
      apiFetch<void>(`/anexos/${anexoId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId, "anexos"] });
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] });
    },
  });
}

// ---------- ASOs ----------
export function useAsos(filtros: { empresa_id?: number | null; q?: string } = {}) {
  const params = new URLSearchParams();
  if (filtros.empresa_id != null) params.set("empresa_id", String(filtros.empresa_id));
  if (filtros.q) params.set("q", filtros.q);
  return useQuery<Aso[]>({
    queryKey: ["asos", filtros],
    queryFn: () => apiFetch<Aso[]>(`/asos?${params.toString()}`),
  });
}

export function useGerarAso(atendimentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<AsoDoAtendimento>(`/atendimentos/${atendimentoId}/aso`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["atendimentos", atendimentoId] });
      qc.invalidateQueries({ queryKey: ["asos"] });
    },
  });
}

export function useMarcarAsoImpresso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asoId: number) =>
      apiFetch<AsoDoAtendimento>(`/asos/${asoId}/marcar-impresso`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asos"] });
      qc.invalidateQueries({ queryKey: ["atendimentos"] });
    },
  });
}

/** Fecha o ciclo do papel — 422 se não houver anexo `aso_assinado`. */
export function useMarcarAsoAssinado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asoId: number) =>
      apiFetch<AsoDoAtendimento>(`/asos/${asoId}/marcar-assinado`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asos"] });
      qc.invalidateQueries({ queryKey: ["atendimentos"] });
    },
  });
}

/** Cancela o ASO (motivo obrigatório, mín. 3 caracteres) e destrava o atendimento. */
export function useCancelarAso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asoId, motivo }: { asoId: number; motivo: string }) =>
      apiFetch<AsoDoAtendimento>(`/asos/${asoId}/cancelar`, {
        method: "POST",
        json: { motivo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asos"] });
      qc.invalidateQueries({ queryKey: ["atendimentos"] });
    },
  });
}

// ---------- Catálogos globais (aptidões e exames) ----------
// São os dois cadastros que não pertencem a empresa nem a documento. A
// contagem de uso vem junto de propósito: é ela que permite decidir se dá pra
// renomear ou mesclar sem quebrar o que já está em operação.
export interface AptidaoCatalogo {
  id: number;
  chave: string;
  nome: string;
  frase_aso: string;
  usos: number;
}

export interface ExameCatalogo {
  id: number;
  nome: string;
  usos_ghe: number;
  usos_realizados: number;
}

export function useAptidoes() {
  return useQuery<AptidaoCatalogo[]>({
    queryKey: ["catalogo", "aptidoes"],
    queryFn: () => apiFetch<AptidaoCatalogo[]>("/aptidoes"),
  });
}

export function useCriarAptidao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { nome: string; frase_aso?: string }) =>
      apiFetch<AptidaoCatalogo>("/aptidoes", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalogo"] }),
  });
}

export function useRenomearAptidao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; nome?: string; frase_aso?: string }) =>
      apiFetch<AptidaoCatalogo>(`/aptidoes/${id}`, { method: "PATCH", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalogo"] }),
  });
}

export function useMesclarAptidao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, destino_id }: { id: number; destino_id: number }) =>
      apiFetch<AptidaoCatalogo>(`/aptidoes/${id}/mesclar`, {
        method: "POST", json: { destino_id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalogo"] }),
  });
}

export function useCatalogoExames(q: string) {
  return useQuery<ExameCatalogo[]>({
    queryKey: ["catalogo", "exames", q],
    queryFn: () => apiFetch<ExameCatalogo[]>(`/catalogo-exames?q=${encodeURIComponent(q)}`),
  });
}

export function useRenomearExame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nome }: { id: number; nome: string }) =>
      apiFetch<ExameCatalogo>(`/catalogo-exames/${id}`, { method: "PATCH", json: { nome } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalogo"] }),
  });
}

export function useMesclarExame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, destino_id }: { id: number; destino_id: number }) =>
      apiFetch<ExameCatalogo>(`/catalogo-exames/${id}/mesclar`, {
        method: "POST", json: { destino_id },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalogo"] }),
  });
}

// ---------- Aptidões por GSE (depois da aprovação) ----------
export interface AptidaoDoGhe {
  id: number;
  chave: string;
  nome: string;
  /** 'extracao' = veio do PCMSO; 'manual' = incluída à mão depois */
  origem: "extracao" | "manual";
}

export interface GheDoDocumento {
  id: number;
  codigo: string | null;
  nome: string | null;
  atendimentos_abertos: number;
  aptidoes: AptidaoDoGhe[];
  funcoes: string[];
}

/** Aptidão incluída à mão no documento anterior que não veio na revisão. */
export interface AptidaoHerdavel {
  ghe_id: number;
  codigo: string;
  chave: string;
  nome: string;
  chaves_atuais: string[];
}

export function useGhesDoDocumento(documentoId: number | null) {
  return useQuery<GheDoDocumento[]>({
    queryKey: ["documentos", documentoId, "ghes"],
    queryFn: () => apiFetch<GheDoDocumento[]>(`/documentos/${documentoId}/ghes`),
    enabled: documentoId !== null,
  });
}

export function useAptidoesHerdaveis(documentoId: number | null) {
  return useQuery<AptidaoHerdavel[]>({
    queryKey: ["documentos", documentoId, "herdaveis"],
    queryFn: () => apiFetch<AptidaoHerdavel[]>(`/documentos/${documentoId}/aptidoes-herdaveis`),
    enabled: documentoId !== null,
  });
}

export function useDefinirAptidoesDoGhe(documentoId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ gheId, chaves }: { gheId: number; chaves: string[] }) =>
      apiFetch(`/ghes/${gheId}/aptidoes`, { method: "PUT", json: { chaves } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documentos", documentoId] }),
  });
}
